import { getProtocolAdapter, ProtocolAdapter, ProtocolType } from './protocols';
import {
  DEFAULT_TEMP_LIMITS_C,
  DEFAULT_TEMP_LIMITS_F,
  UNIVERSAL_PROPERTIES,
  UniversalProperty,
} from './properties';

/**
 * Options required to synthesize a standard unit shape from a universal device entry.
 */
export interface ToUnitShapeOptions {
  /** The raw device entry (BACnet or Modbus) */
  entry: any;
  /** The parameter template (for BACnet) */
  paramTemplate?: any;
  /** The device rows (for Modbus) */
  rows?: any[];
  /** Current operational data points map */
  operationalData: Record<string, any>;
  /** Optional Redux/EasyPeasy types store for mode enum mirroring */
  typesStore?: any;
  /** Whether the active temperature unit is Celsius (defaults to true) */
  isCelsius?: boolean;
  /** The device protocol adapter to use (defaults to 'bacnet') */
  protocol?: ProtocolType;
}

/**
 * Resolves supported modes and active mode from universal points configuration.
 */
export const resolveModes = (
  hasProperty: boolean,
  point: any,
  rawValue: any,
  mirror: Record<string, any> | undefined,
  systemEnum: Record<string | number, string> | undefined,
  adapter: ProtocolAdapter
): { supportedModes: number[]; activeMode: number } => {
  let supportedModes: number[] = [];
  let activeMode = 0;

  if (!hasProperty || !point) {
    return { supportedModes, activeMode };
  }

  const modeConfig = point?.config || point?.row || point;
  const propertyMapping = modeConfig?.propertyMapping;
  const customEnum = modeConfig?.customEnum;

  if (propertyMapping) {
    supportedModes = Object.keys(propertyMapping)
      .map((k) => Number(k))
      .filter((n) => !isNaN(n));

    if (rawValue !== null && rawValue !== undefined) {
      const foundKey = Object.keys(propertyMapping).find(
        (k) => String(propertyMapping[k]) === String(rawValue)
      );
      if (foundKey !== undefined) {
        activeMode = Number(foundKey);
      }
    }
  } else if (customEnum) {
    const enumKeys = Object.keys(customEnum);
    const safeMirror = mirror || {};

    enumKeys.forEach((key) => {
      const numKey = Number(key);
      const val = customEnum[key];
      if (typeof val === 'string' && safeMirror[val.toUpperCase()] !== undefined) {
        const mappedIdx = Number(safeMirror[val.toUpperCase()]);
        if (!isNaN(mappedIdx) && !supportedModes.includes(mappedIdx)) {
          supportedModes.push(mappedIdx);
        }
      } else if (!isNaN(numKey) && !supportedModes.includes(numKey)) {
        supportedModes.push(numKey);
      }
    });

    const formattedVal = adapter.formatValue(rawValue, modeConfig, systemEnum);
    if (
      typeof formattedVal === 'string' &&
      safeMirror[formattedVal.toUpperCase()] !== undefined
    ) {
      activeMode = Number(safeMirror[formattedVal.toUpperCase()]);
    } else {
      const numRaw = Number(rawValue);
      activeMode = !isNaN(numRaw) ? numRaw : 0;
    }
  } else {
    const numRaw = Number(rawValue);
    if (!isNaN(numRaw)) {
      supportedModes = [numRaw];
      activeMode = numRaw;
    }
  }

  return { supportedModes, activeMode };
};

/**
 * Helper to resolve raw device mode value from a UI mode index for a given property.
 */
export const resolveRawValueForProperty = (
  point: any,
  newIndex: number,
  typesStore: any,
  property: UniversalProperty = UNIVERSAL_PROPERTIES.OPERATION_MODE
): any => {
  let rawValue: any = newIndex;
  const config = point?.config || point?.row || point;

  if (config?.propertyMapping) {
    const mappedRaw = config.propertyMapping[String(newIndex)];
    if (mappedRaw !== undefined) {
      return mappedRaw;
    }
    return newIndex;
  }

  if (config?.customEnum) {
    const customEnum = config.customEnum;
    let systemEnum: Record<string | number, string> = {};
    if (property === UNIVERSAL_PROPERTIES.OPERATION_MODE) {
      systemEnum =
        typesStore?.types?.operationModesExtended || typesStore?.operationModesExtended || {};
    } else if (property === UNIVERSAL_PROPERTIES.FAN_MODE) {
      systemEnum =
        typesStore?.types?.fanModesExtended ||
        typesStore?.types?.fanModes ||
        typesStore?.fanModesExtended ||
        {};
    } else if (property === UNIVERSAL_PROPERTIES.SWING_MODE) {
      systemEnum = typesStore?.types?.swingModes || typesStore?.swingModes || {};
    }

    const modeName = systemEnum[newIndex];
    if (modeName) {
      const foundEnumKey = Object.keys(customEnum).find(
        (k) => customEnum[k]?.toUpperCase() === modeName.toUpperCase()
      );
      if (foundEnumKey !== undefined) {
        return Number(foundEnumKey);
      }
    }
  }

  return rawValue;
};

/**
 * Helper to resolve raw device mode value from a UI mode index.
 */
export const resolveRawModeValue = (point: any, newModeIndex: number, typesStore: any): any => {
  return resolveRawValueForProperty(point, newModeIndex, typesStore, UNIVERSAL_PROPERTIES.OPERATION_MODE);
};

export const resolveRawFanModeValue = (point: any, newIndex: number, typesStore: any): any => {
  return resolveRawValueForProperty(point, newIndex, typesStore, UNIVERSAL_PROPERTIES.FAN_MODE);
};

export const resolveRawSwingModeValue = (point: any, newIndex: number, typesStore: any): any => {
  return resolveRawValueForProperty(point, newIndex, typesStore, UNIVERSAL_PROPERTIES.SWING_MODE);
};

/**
 * Resolves supported operation modes and active operation mode from universal points configuration.
 */
export const resolveOperationModes = (
  hasMode: boolean,
  modePoint: any,
  rawMode: any,
  typesStore: any,
  adapter: ProtocolAdapter
): { supportedOperationModes: number[]; activeOperationMode: number } => {
  const { supportedModes, activeMode } = resolveModes(
    hasMode,
    modePoint,
    rawMode,
    typesStore?.operationModesMirror,
    typesStore?.types?.operationModesExtended || typesStore?.operationModesExtended,
    adapter
  );

  return { supportedOperationModes: supportedModes, activeOperationMode: activeMode };
};

/**
 * Transforms a protocol-agnostic universal device entry into a standard HVAC Unit shape
 * consumable by the standard UnitPanel component.
 */
export const toUnitShape = ({
  entry,
  paramTemplate,
  rows,
  operationalData,
  typesStore,
  isCelsius = true,
  protocol = 'bacnet',
}: ToUnitShapeOptions) => {
  const adapter = getProtocolAdapter(protocol);
  const sourceData =
    protocol === 'modbus'
      ? rows || entry?.rows || entry?.paramTemplateData?.rows || []
      : paramTemplate?.data || entry?.paramTemplateData || {};

  const universalMappedPoints = adapter.getUniversalMappedPoints(sourceData);

  const hasPower = !!universalMappedPoints[UNIVERSAL_PROPERTIES.OPERATION_STATUS];
  const hasMode = !!universalMappedPoints[UNIVERSAL_PROPERTIES.OPERATION_MODE];
  const hasFan = !!universalMappedPoints[UNIVERSAL_PROPERTIES.FAN_MODE];
  const hasSwing = !!universalMappedPoints[UNIVERSAL_PROPERTIES.SWING_MODE];
  const hasSetpoint = !!universalMappedPoints[UNIVERSAL_PROPERTIES.SETPOINT];
  const hasRoomTemp = !!universalMappedPoints[UNIVERSAL_PROPERTIES.ROOM_TEMPERATURE];

  const rawPower = hasPower
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.OPERATION_STATUS,
        universalMappedPoints,
        operationalData
      )
    : null;
  const rawSetpoint = hasSetpoint
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.SETPOINT,
        universalMappedPoints,
        operationalData
      )
    : null;
  const rawRoomTemp = hasRoomTemp
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.ROOM_TEMPERATURE,
        universalMappedPoints,
        operationalData
      )
    : null;
  const rawMode = hasMode
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.OPERATION_MODE,
        universalMappedPoints,
        operationalData
      )
    : null;
  const rawFan = hasFan
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.FAN_MODE,
        universalMappedPoints,
        operationalData
      )
    : null;
  const rawSwing = hasSwing
    ? adapter.getOperationalValue(
        UNIVERSAL_PROPERTIES.SWING_MODE,
        universalMappedPoints,
        operationalData
      )
    : null;

  // 1. Power State
  // The device speaks raw 1/0; UnitPanel speaks the app's operationStatuses
  // enum, whose on/off values come from the server and are NOT 1/0. Translate
  // through the mirror so the power round-trip closes — hardcoding 1/0 here
  // leaves the panel unable to turn a unit back on.
  const isPowerOn =
    rawPower === 1 ||
    rawPower === '1' ||
    rawPower === true ||
    rawPower === 'ON' ||
    rawPower === 'on';
  const statusMirror = typesStore?.operationStatusesMirror;
  const activeOperationStatus = isPowerOn
    ? statusMirror?.on !== undefined
      ? +statusMirror.on
      : 1
    : statusMirror?.off !== undefined
    ? +statusMirror.off
    : 0;

  // 2. Setpoint
  const parsedSetpoint = Number(rawSetpoint);
  const activeSetpoint =
    rawSetpoint !== null && !isNaN(parsedSetpoint) ? parsedSetpoint : isCelsius ? 22 : 72;

  // 3. Room Temperature
  const parsedRoomTemp = Number(rawRoomTemp);
  const ambientTemperature =
    rawRoomTemp !== null && !isNaN(parsedRoomTemp) ? parsedRoomTemp : undefined;

  // 4. Operation Mode Resolution & Supported Modes
  const modePoint = universalMappedPoints[UNIVERSAL_PROPERTIES.OPERATION_MODE];
  const { supportedOperationModes, activeOperationMode } = resolveOperationModes(
    hasMode,
    modePoint,
    rawMode,
    typesStore,
    adapter
  );

  // Fan Mode Resolution & Supported Modes
  const fanPoint = universalMappedPoints[UNIVERSAL_PROPERTIES.FAN_MODE];
  const { supportedModes: supportedFanModes, activeMode: activeFanMode } = resolveModes(
    hasFan,
    fanPoint,
    rawFan,
    typesStore?.fanModesMirror,
    typesStore?.types?.fanModesExtended || typesStore?.types?.fanModes || typesStore?.fanModesExtended,
    adapter
  );

  // Swing Mode Resolution & Supported Modes
  const swingPoint = universalMappedPoints[UNIVERSAL_PROPERTIES.SWING_MODE];
  const { supportedModes: supportedSwingModes, activeMode: activeSwingMode } = resolveModes(
    hasSwing,
    swingPoint,
    rawSwing,
    typesStore?.swingModesMirror,
    typesStore?.types?.swingModes || typesStore?.swingModes,
    adapter
  );

  // 5. Temperature Limits
  //
  // The range must be a real span. Modbus rows type minValue/maxValue as
  // `number | null`, and an unconfigured row carries null on both - which
  // coerces to a degenerate [0, 0]. The control screen divides by
  // (endTemp - startTemp) to position the setpoint colour, so a zero-width
  // range yields Infinity and paints the whole dial ring black. Anything that
  // isn't a genuine min < max span falls back to the sane default.
  const setpointPoint = universalMappedPoints[UNIVERSAL_PROPERTIES.SETPOINT];
  const setpointConfig = setpointPoint?.config || setpointPoint?.row || setpointPoint;
  const configuredMin = Number(setpointConfig?.minValue);
  const configuredMax = Number(setpointConfig?.maxValue);
  const hasUsableRange =
    setpointConfig?.minValue != null &&
    setpointConfig?.maxValue != null &&
    !isNaN(configuredMin) &&
    !isNaN(configuredMax) &&
    configuredMax > configuredMin;

  // Falls back to the very same constant a regular unit uses, so an identical
  // setpoint paints an identical dial colour on both screens.
  const limitsRange: [number, number] = hasUsableRange
    ? [configuredMin, configuredMax]
    : isCelsius ? DEFAULT_TEMP_LIMITS_C : DEFAULT_TEMP_LIMITS_F;

  const temperatureLimits: Record<number, [number, number]> = {
    0: limitsRange,
    1: limitsRange,
    2: limitsRange,
    3: limitsRange,
    4: limitsRange,
    5: limitsRange,
    6: limitsRange,
    7: limitsRange,
  };

  // 6. Capability Flags & Mode Enablement
  const enableCoolMode = !hasMode || supportedOperationModes.includes(0);
  const enableHeatMode = !hasMode || supportedOperationModes.includes(1);
  const enableAutoMode = !hasMode || supportedOperationModes.includes(2);

  const setpointControlModes: Record<number, boolean> = {};
  const allPossibleModes =
    supportedOperationModes.length > 0 ? supportedOperationModes : [0, 1, 2, 3, 4, 5, 6, 7];
  allPossibleModes.forEach((m) => {
    setpointControlModes[m] = hasSetpoint;
  });

  const capabilityFlags = {
    enableOperationStatusSelection: hasPower,
    enableOperationModeSelection: hasMode && supportedOperationModes.length > 0,
    enableSetpointControl: hasSetpoint,
    enableAmbientTemperature: hasRoomTemp,
    setpointControlModes,
    enableFanModeSelection: hasFan && supportedFanModes.length > 0,
    enableSwingModeSelection: hasSwing && supportedSwingModes.length > 0,
  };

  // 7. Permissions
  const permissions = {
    canControlOperationStatus: hasPower,
    canControlOperationMode: hasMode,
    canControlSetpoint: hasSetpoint,
    canControlFanMode: hasFan,
    canControlSwingMode: hasSwing,
    canReadSchedules: !!entry?.permissions?.canReadSchedules || !!entry?.schedules?.length,
    canReadTimers: false,
    canCreateTimers: false,
  };

  return {
    id: entry?.id || '',
    name: entry?.name || entry?.title || '',
    title: entry?.title || entry?.name || '',
    activeOperationStatus,
    activeOperationMode,
    activeSetpoint,
    ambientTemperature,
    supportedOperationModes,
    supportedFanModes,
    supportedSwingModes,
    supportedCurveTempModes: supportedFanModes,
    activeFanMode,
    activeCurveTempMode: activeFanMode,
    activeSwingMode,
    enableCoolMode,
    enableHeatMode,
    enableAutoMode,
    enableSetpoint: false,
    enableMode: false,
    enableOnState: false,
    capabilityFlags,
    permissions,
    temperatureLimits,
    subType: 0,
    isHalfCDegreeEnabled: false,
    isConnected: entry?.isConnected ?? true,
    schedules: entry?.schedules || [],
    isUniversalUnit: true,
    universalMappedPoints,
  };
};
